import type React from "react";
import {useState} from "react";
import {IconButton, ListItemIcon, ListItemText, Menu, MenuItem} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import type {DashboardItem} from "./services/dashboardsApi";
import {DashboardThumb} from "./DashboardThumb";
import {dashboardMeta, VIEW_ONLY} from "./dashboards.utils";

type Props = {
    dashboard: DashboardItem;
    onOpen: (d: DashboardItem) => void;
    /** Rename / Delete appear only when given (Yours). */
    onRename?: (d: DashboardItem) => void;
    onDelete?: (d: DashboardItem) => void;
};

export const DashboardCard: React.FC<Props> = ({dashboard, onOpen, onRename, onDelete}) => {
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const hasMenu = Boolean(onRename || onDelete);
    const meta = dashboardMeta(dashboard);

    return (
        <div
            className="dsh-gcard"
            role="link"
            tabIndex={0}
            aria-label={dashboard.title}
            onClick={() => onOpen(dashboard)}
            onKeyDown={e => {
                if (e.target === e.currentTarget && e.key === "Enter") onOpen(dashboard);
            }}
        >
            <DashboardThumb dashboard={dashboard}/>
            <div className="dsh-gcard-b">
                <div className="dsh-gcard-t">
                    <span className="dsh-t-card-title dsh-ell" title={dashboard.title}>{dashboard.title}</span>
                    {hasMenu && (
                        <IconButton
                            size="small"
                            className="dsh-gmore"
                            aria-label={`More actions for ${dashboard.title}`}
                            aria-expanded={Boolean(anchor)}
                            onClick={e => {
                                e.stopPropagation();
                                setAnchor(e.currentTarget);
                            }}
                        >
                            <MoreVertIcon sx={{fontSize: 16}}/>
                        </IconButton>
                    )}
                </div>
                {dashboard.description && <p className="dsh-t-caption dsh-gdesc">{dashboard.description}</p>}
                <div className="dsh-gmeta dsh-t-caption">
                    {meta.map((m, i) => (
                        <span key={m} className="dsh-gmeta-part">
                            {i > 0 && <span className="dsh-dot" aria-hidden="true">·</span>}
                            {m === VIEW_ONLY ? (
                                <span className="dsh-lock">
                                    <LockOutlinedIcon sx={{fontSize: 14}}/> View only
                                </span>
                            ) : m}
                        </span>
                    ))}
                </div>
            </div>
            {hasMenu && (
                <Menu
                    anchorEl={anchor}
                    open={Boolean(anchor)}
                    onClose={() => setAnchor(null)}
                    onClick={e => e.stopPropagation()}
                    disableRestoreFocus
                    slotProps={{paper: {sx: {minWidth: 184}}}}
                >
                    {onRename && (
                        <MenuItem onClick={() => {
                            setAnchor(null);
                            onRename(dashboard);
                        }}>
                            <ListItemIcon><EditOutlinedIcon fontSize="small"/></ListItemIcon>
                            <ListItemText>Rename</ListItemText>
                        </MenuItem>
                    )}
                    {onDelete && (
                        <MenuItem
                            sx={{color: "rgb(186, 26, 26)"}}
                            onClick={() => {
                                setAnchor(null);
                                onDelete(dashboard);
                            }}
                        >
                            <ListItemIcon sx={{color: "inherit"}}><DeleteOutlineIcon fontSize="small"/></ListItemIcon>
                            <ListItemText>Delete</ListItemText>
                        </MenuItem>
                    )}
                </Menu>
            )}
        </div>
    );
};
